export interface CompactTokenMap {
  schemaVersion: '1.0';
  chapterId: string;
  tokens: string[];
  blocks: number[];
  sentences: Array<[number, number]>;
}

export interface CompactTimingMap {
  schemaVersion: '1.0';
  chapterId: string;
  voiceKey: string;
  timebase: 'ms_from_chapter_audio_start';
  startsMs: number[];
  endsMs: number[];
}

export interface ChapterRuntime {
  tokenMap: CompactTokenMap;
  timings: CompactTimingMap;
}

export interface MountedChapterIndex {
  root: HTMLElement;
  segmentsByOrdinal: HTMLElement[][];
  ordinalByElement: WeakMap<Element, number>;
  blockElementByOrdinal: Array<HTMLElement | null>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

export const parseCompactTokenMap = (value: unknown, expectedChapterId: string): CompactTokenMap => {
  if (!isRecord(value)) {
    throw new Error('Token map payload must be an object');
  }
  if (value.schema_version !== '1.0' || value.chapter_id !== expectedChapterId) {
    throw new Error('Token map schema or chapter does not match the request');
  }
  if (!Array.isArray(value.tokens) || !value.tokens.every((token) => typeof token === 'string')) {
    throw new Error('Token map tokens must be a string array');
  }
  if (!Array.isArray(value.blocks) || !value.blocks.every(isNonNegativeInteger)) {
    throw new Error('Token map blocks must be a non-negative integer array');
  }
  if (value.tokens.length !== value.blocks.length) {
    throw new Error('Token and block arrays must have equal lengths');
  }
  if (!Array.isArray(value.sentences)) {
    throw new Error('Token map sentences must be an array');
  }

  const tokenCount = value.tokens.length;
  let previousEnd = -1;
  const sentences = value.sentences.map((range, index): [number, number] => {
    if (
      !Array.isArray(range) ||
      range.length !== 2 ||
      !isNonNegativeInteger(range[0]) ||
      !isNonNegativeInteger(range[1]) ||
      range[0] > range[1] ||
      range[1] >= tokenCount ||
      range[0] <= previousEnd
    ) {
      throw new Error(`Invalid inclusive sentence range at index ${index}`);
    }
    previousEnd = range[1];
    return [range[0], range[1]];
  });

  return {
    schemaVersion: '1.0',
    chapterId: expectedChapterId,
    tokens: [...value.tokens],
    blocks: [...value.blocks],
    sentences,
  };
};

export const parseCompactTimingMap = (
  value: unknown,
  expectedChapterId: string,
  expectedVoiceKey: string,
): CompactTimingMap => {
  if (!isRecord(value)) {
    throw new Error('Timing payload must be an object');
  }
  if (
    value.schema_version !== '1.0' ||
    value.chapter_id !== expectedChapterId ||
    value.voice_key !== expectedVoiceKey ||
    value.timebase !== 'ms_from_chapter_audio_start'
  ) {
    throw new Error('Timing schema, chapter, voice, or timebase does not match the request');
  }
  if (!Array.isArray(value.starts_ms) || !value.starts_ms.every(isNonNegativeInteger)) {
    throw new Error('Timing starts_ms must be a non-negative integer array');
  }
  if (!Array.isArray(value.ends_ms) || !value.ends_ms.every(isNonNegativeInteger)) {
    throw new Error('Timing ends_ms must be a non-negative integer array');
  }
  if (value.starts_ms.length !== value.ends_ms.length) {
    throw new Error('Timing start and end arrays must have equal lengths');
  }

  for (let index = 0; index < value.starts_ms.length; index += 1) {
    const start = value.starts_ms[index];
    const end = value.ends_ms[index];
    if (end < start) {
      throw new Error(`Timing end precedes start at ordinal ${index}`);
    }
    if (
      index > 0 &&
      (start < value.starts_ms[index - 1] || end < value.ends_ms[index - 1])
    ) {
      throw new Error(`Timing arrays are not monotonic at ordinal ${index}`);
    }
  }

  return {
    schemaVersion: '1.0',
    chapterId: expectedChapterId,
    voiceKey: expectedVoiceKey,
    timebase: 'ms_from_chapter_audio_start',
    startsMs: [...value.starts_ms],
    endsMs: [...value.ends_ms],
  };
};

export const buildMountedChapterIndex = (root: HTMLElement): MountedChapterIndex => {
  const segmentsByOrdinal: HTMLElement[][] = [];
  const ordinalByElement = new WeakMap<Element, number>();
  const blockElementByOrdinal: Array<HTMLElement | null> = [];
  const elements = Array.from(root.querySelectorAll<HTMLElement>('[data-w], [data-w-part]'));
  let ordinal = -1;

  for (const element of elements) {
    const isPrimary = element.hasAttribute('data-w');
    const isContinuation = element.hasAttribute('data-w-part');
    if (isPrimary) {
      ordinal += 1;
      segmentsByOrdinal[ordinal] = [element];
      blockElementByOrdinal[ordinal] = element.closest<HTMLElement>('[data-block-id]');
      if (isContinuation && import.meta.env.DEV) {
        console.warn('[reader] Token element has both data-w and data-w-part; treating it as a primary token.');
      }
    } else if (isContinuation) {
      if (ordinal < 0) {
        throw new Error('Encountered data-w-part before the first data-w token');
      }
      segmentsByOrdinal[ordinal].push(element);
    }
    if (ordinal >= 0) {
      ordinalByElement.set(element, ordinal);
    }
  }

  return { root, segmentsByOrdinal, ordinalByElement, blockElementByOrdinal };
};

export const resolveTimingOrdinal = (
  startsMs: readonly number[],
  endsMs: readonly number[],
  currentMs: number,
): number => {
  if (startsMs.length === 0 || startsMs.length !== endsMs.length || currentMs < startsMs[0]) {
    return -1;
  }

  let low = 0;
  let high = startsMs.length - 1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    if (startsMs[middle] <= currentMs) {
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  const ordinal = high;
  if (ordinal < 0 || (ordinal === startsMs.length - 1 && currentMs > endsMs[ordinal])) {
    return -1;
  }
  return ordinal;
};

export const findSentenceIndexForOrdinal = (
  sentences: ReadonlyArray<readonly [number, number]>,
  ordinal: number,
): number => {
  let low = 0;
  let high = sentences.length - 1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const [start, end] = sentences[middle];
    if (ordinal < start) {
      high = middle - 1;
    } else if (ordinal > end) {
      low = middle + 1;
    } else {
      return middle;
    }
  }
  return -1;
};

export const findBlockStartOrdinal = (blocks: readonly number[], ordinal: number): number => {
  if (ordinal < 0 || ordinal >= blocks.length) {
    return Math.max(0, Math.min(ordinal, blocks.length - 1));
  }
  const block = blocks[ordinal];
  let start = ordinal;
  while (start > 0 && blocks[start - 1] === block) {
    start -= 1;
  }
  return start;
};

// A token whose text ends a sentence: . ! ? … optionally followed by closing
// quotes/brackets.
const SENTENCE_END = /[.!?…]['")\]”’]*$/;
// Caps the walk-back so a block with no terminal punctuation can't rewind
// arbitrarily far (the longest reasonable sentence is well under this).
const MAX_SENTENCE_LOOKBACK = 250;

// The publisher currently emits one `sentences` range per block, i.e. per
// paragraph, so the range start alone would rewind to the paragraph start.
// Walk back within that range to the token after the last sentence-ending one.
export const findSentenceStartOrdinal = (tokenMap: CompactTokenMap, ordinal: number): number => {
  const sentenceIndex = findSentenceIndexForOrdinal(tokenMap.sentences, ordinal);
  const rangeStart = sentenceIndex >= 0
    ? tokenMap.sentences[sentenceIndex][0]
    : findBlockStartOrdinal(tokenMap.blocks, ordinal);
  const limit = Math.max(rangeStart, ordinal - MAX_SENTENCE_LOOKBACK);
  let start = ordinal;
  while (start > limit && !SENTENCE_END.test((tokenMap.tokens[start - 1] ?? '').trim())) {
    start -= 1;
  }
  return start;
};

export const buildCompactContext = (
  tokenMap: CompactTokenMap,
  ordinal: number,
  wordBudget: number = 100,
): string => {
  if (ordinal < 0 || tokenMap.tokens.length === 0 || wordBudget <= 0) {
    return '';
  }
  const end = Math.min(ordinal, tokenMap.tokens.length - 1);
  const sentenceIndex = findSentenceIndexForOrdinal(tokenMap.sentences, end);
  const candidateStart = sentenceIndex >= 0
    ? tokenMap.sentences[0][0]
    : findBlockStartOrdinal(tokenMap.blocks, end);
  const selected: string[] = [];
  let words = 0;
  for (let index = end; index >= candidateStart; index -= 1) {
    const token = tokenMap.tokens[index];
    selected.unshift(token);
    words += token.trim().split(/\s+/).filter(Boolean).length;
    if (words >= wordBudget) {
      break;
    }
  }
  return selected.join(' ').replace(/\s+([,.;:!?])/g, '$1').trim();
};

export const getSentenceFingerprint = (tokenMap: CompactTokenMap, ordinal: number): string => {
  const sentenceIndex = findSentenceIndexForOrdinal(tokenMap.sentences, ordinal);
  if (sentenceIndex < 0) {
    return (tokenMap.tokens[ordinal] ?? '').slice(0, 50);
  }
  const [start, end] = tokenMap.sentences[sentenceIndex];
  return tokenMap.tokens
    .slice(start, end + 1)
    .join(' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .slice(0, 50);
};
